import * as React from 'react'
import { FC, useEffect, useState } from 'react'
import { useAppDispatch } from '@user/store/hooks'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Skeleton from '@mui/material/Skeleton'
import {
  deleteTerritory,
  fetchTerritoryRuleUsage,
} from '@user/store/territory/api'
import { Territory, TerritoryRuleUsage } from '@user/store/territory/typings'

type Props = {
  territory: Territory
  onCancel: () => void
  onDeleted: () => void
}

/**
 * Confirms deleting a territory (WWM-17508, Feature A).
 *
 * Deliberately a sibling of the territory list rather than nested inside it: the caller closes
 * the list and moves the map to the territory first, so this sits over the shape being deleted.
 * Confirming a destructive action against a name alone is how the wrong one gets removed --
 * several of these are called things like "Monmouth North" and "Monmouth South".
 *
 * It also names the assignment rules the delete will edit. A territory can be a condition in a
 * rule, and deleting it takes the territory out of them -- disabling any rule that has nothing
 * else to match on, because a rule with no conditions matches every lead. The objection to
 * territories being rule conditions at all was that territories churn while rules do not; saying
 * out loud which rules change, before the delete, is the answer to it.
 */
export const TerritoryDeleteDialog: FC<Props> = ({
  territory,
  onCancel,
  onDeleted,
}) => {
  const dispatch = useAppDispatch()
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [usage, setUsage] = useState<TerritoryRuleUsage[] | null>(null)
  const [usageFailed, setUsageFailed] = useState(false)

  // Asked when the dialog opens rather than held in the slice: it is a question about rules, and
  // a cached answer could have the admin confirming against a rule list that has since changed.
  useEffect(() => {
    let cancelled = false

    const run = async () => {
      const result = await dispatch(fetchTerritoryRuleUsage(territory.id))

      if (cancelled) return

      if (fetchTerritoryRuleUsage.rejected.match(result)) {
        // Soft-fail: not knowing which rules reference the territory must not block the delete,
        // but the admin should be told the warning is missing rather than reading its absence
        // as "no rules use this".
        setUsageFailed(true)
        setUsage([])
        return
      }

      setUsage(result.payload as TerritoryRuleUsage[])
    }

    run()
    return () => {
      cancelled = true
    }
  }, [dispatch, territory.id])

  const disabledByDelete = usage?.filter((rule) => rule.willBeDisabled) ?? []

  const confirm = async () => {
    setDeleting(true)
    setError(null)

    const result = await dispatch(deleteTerritory(territory.id))

    setDeleting(false)

    if (deleteTerritory.rejected.match(result)) {
      setError(
        result.payload?.message ??
          'Could not delete this territory. Please try again.',
      )
      return
    }

    onDeleted()
  }

  return (
    <Dialog
      open
      onClose={deleting ? undefined : onCancel}
      maxWidth="xs"
      // Lighter than the default 0.5 backdrop. The caller has just centred the map on the
      // territory, and territories render at 0.1 fill opacity by design -- under a standard
      // backdrop the thing being confirmed would be all but invisible, which would make the
      // sentence below a lie.
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.2)' } },
      }}
    >
      <DialogTitle>Delete this territory?</DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <DialogContentText>
          {/* Names the consequence, not just the act: a territory decides who new leads
              inside it are assigned to, so removing one quietly changes assignment there. */}
          The map behind this message is now centred on &ldquo;
          {territory.name}&rdquo;. Deleting it means new leads in that area will
          no longer be assigned to its owners.
        </DialogContentText>

        {usage === null ? (
          <Skeleton height={24} sx={{ mt: 1 }} />
        ) : usageFailed ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            Could not check which assignment rules use this territory. Deleting
            it will still remove it from any that do.
          </Alert>
        ) : (
          usage.length > 0 && (
            <Alert
              severity={disabledByDelete.length > 0 ? 'warning' : 'info'}
              sx={{ mt: 2 }}
              data-test-id="territory-delete-rule-usage"
            >
              {/* The rules are named, not counted. "Used by 2 rules" tells an admin they
                  have a problem; naming them tells them which one. */}
              Used by{' '}
              {usage.length === 1 ? 'the assignment rule' : 'assignment rules'}{' '}
              {usage.map((rule) => `“${rule.name}”`).join(', ')}. Deleting this
              territory removes it from{' '}
              {usage.length === 1 ? 'that rule' : 'those rules'}.
              {disabledByDelete.length > 0 && (
                <>
                  {' '}
                  {disabledByDelete.length === 1
                    ? `“${disabledByDelete[0].name}” has nothing else to match on, so it will be turned off.`
                    : `${disabledByDelete.length} of them have nothing else to match on, so they will be turned off.`}
                </>
              )}
            </Alert>
          )
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} disabled={deleting}>
          Keep it
        </Button>
        <Button
          onClick={confirm}
          color="error"
          disabled={deleting}
          data-test-id="territory-delete-confirm"
        >
          {deleting ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
